import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Button, Form, Spinner } from "react-bootstrap";
import {
  fetchAddresses,
  selectAddresses,
  selectAddressesLoading,
} from "../../redux/features/addressSlice";
import { policiesService } from "../../services/policies";
import AddressForm from "../policies/AddressForm";
import "./AddressDropdown.css";

function AddressDropdown({
  policyId,
  selectedAddressId,
  onSelectAddress,
  onAddNewAddress,
}) {
  const userDetails = useSelector((state) => state.auth.userDetails);
  const {role: userRole } = userDetails;

  const dispatch = useDispatch();
  const addresses = useSelector(selectAddresses);
  const loading = useSelector(selectAddressesLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formattedAddresses, setFormattedAddresses] = useState([]);

  useEffect(() => {
    if (policyId) {
      dispatch(fetchAddresses(policyId));
    }
  }, [policyId, dispatch]);

  useEffect(() => {
    // Format addresses for display
    const formatted = addresses.map((addr) => ({
      ...addr,
      fullAddress: `${addr.address_line_1}${
        addr.address_line_2 ? ", " + addr.address_line_2 : ""
      }, ${addr.city}, ${addr.state} ${addr.zip}`,
    }));
    setFormattedAddresses(formatted);
  }, [addresses]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAddresses = formattedAddresses.filter((addr) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      addr.address_line_1?.toLowerCase().includes(searchLower) ||
      addr.address_line_2?.toLowerCase().includes(searchLower) ||
      addr.city?.toLowerCase().includes(searchLower) ||
      addr.state?.toLowerCase().includes(searchLower) ||
      addr.zip?.toLowerCase().includes(searchLower)
    );
  });

  const selectedAddress = formattedAddresses.find(
    (addr) => addr.id === selectedAddressId
  );

  const handleAddressFormSuccess = (id) => {
    setShowAddressForm(false);
    setSearchTerm("");
    if (onAddNewAddress) {
      onAddNewAddress(id);
    }
  };

  if (!policyId) {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Project Address</Form.Label>
        <div className="alert alert-info mb-0">
          Please select a policy first to view project addresses.
        </div>
      </Form.Group>
    );
  }

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Project Address</Form.Label>
        <Dropdown className="w-100 address-dropdown" >
          <Dropdown.Toggle
            variant="outline-secondary"
            id="address-dropdown"
            className="w-100 text-start"
            disabled={userRole !== "Admin" && userRole !== "Tradesman"}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Loading addresses...
              </>
            ) : selectedAddress ? (
              selectedAddress.fullAddress
            ) : (
              "Select an address..."
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="w-100 address-dropdown-menu" >
            <div className="p-2">
              <Form.Control
                placeholder="Search addresses..."
                value={searchTerm}
                onChange={handleSearch}
                className="mb-2"
              />
            </div>

            {loading ? (
              <Dropdown.Item disabled>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Loading...
              </Dropdown.Item>
            ) : filteredAddresses.length === 0 ? (
              <>
                <Dropdown.Item disabled>
                  {searchTerm
                    ? "No addresses match your search"
                    : "No addresses available"}
                </Dropdown.Item>
                {(userRole === "Admin" || userRole === "Tradesman") && <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setShowAddressForm(true)}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add New Address
                </Dropdown.Item>
                </>}
              </>
            ) : (
              <>
                {filteredAddresses.map((addr) => (
                  <Dropdown.Item
                    key={addr.id}
                    onClick={() => {
                      onSelectAddress(addr.id);
                      setSearchTerm("");
                    }}
                    className={selectedAddressId === addr.id ? "active" : ""}
                    title={addr.fullAddress}
                  >
                    <div className="address-item">
                      <div className="address-main">{addr.address_line_1}</div>
                      {addr.address_line_2 && (
                        <div className="address-secondary">
                          {addr.address_line_2}
                        </div>
                      )}
                      <div className="address-city-state">
                        {addr.city}, {addr.state} {addr.zip}
                      </div>
                    </div>
                  </Dropdown.Item>
                ))}
                {(userRole === "Admin" || userRole === "Tradesman") && <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setShowAddressForm(true)}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add New Address
                </Dropdown.Item>
                </>}
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>

      {showAddressForm && (
        <AddressForm
          policyId={policyId}
          address={null}
          onClose={() => setShowAddressForm(false)}
          onSuccess={handleAddressFormSuccess}
        />
      )}
    </>
  );
}

export default AddressDropdown;
